import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { AppShell, Button } from '@dj/ui';
import { callable } from '@dj/firebase-client';
import { useClaim } from '../state/ClaimContext';

const STRIPE_PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

/**
 * Step 3 — payment + claim.
 *
 * Flow:
 *   1. Stripe CardElement collects card details
 *   2. On submit: stripe.createPaymentMethod(card) → paymentMethodId
 *   3. Call `claimEntitlement` CF with code + identity + paymentMethodId
 *      → CF roster-matches, creates Stripe customer + subscription,
 *        writes entitlement, returns success or generic failure reason
 *   4. On success: store result + navigate to /confirmation
 *
 * 100%-subsidy short circuit: if patientPortionCents === 0 we skip the
 * card collection entirely — submit goes straight to claimEntitlement
 * without a paymentMethodId.
 */
export function PaymentPage() {
  const navigate = useNavigate();
  const { provider, identity, claimToken } = useClaim();

  // Claiming needs the emailed link (Tess re-scan 3, R11).
  useEffect(() => {
    if (!provider || !identity || !claimToken) navigate('/', { replace: true });
  }, [provider, identity, claimToken, navigate]);

  if (!provider || !identity || !claimToken) return null;

  // 100% subsidy — no card needed.
  if (provider.patientPortionCents === 0) {
    return (
      <AppShell title="Confirm">
        <ZeroDollarFlow />
      </AppShell>
    );
  }

  if (!STRIPE_PK || !stripePromise) {
    return (
      <AppShell title="Payment">
        <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
          <div className="bg-amber-50 text-amber-900 text-sm p-3 rounded">
            Payment isn't configured for this environment yet. Set
            <code className="mx-1 font-mono">VITE_STRIPE_PUBLISHABLE_KEY</code>
            and rebuild.
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Payment">
      <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">Payment</h1>
        <PaymentSummary />
        <Elements stripe={stripePromise}>
          <PaymentForm />
        </Elements>
        <div className="mt-4">
          <Button variant="ghost" onClick={() => navigate('/')}>Back</Button>
        </div>
      </div>
    </AppShell>
  );
}

function PaymentSummary() {
  const { provider } = useClaim();
  if (!provider) return null;
  const subsidized = (provider.monthlyPriceCents - provider.patientPortionCents) / 100;
  const youPay = provider.patientPortionCents / 100;
  return (
    <div className="border border-slate-200 rounded p-3 mb-4 text-sm">
      <div className="flex justify-between mb-1">
        <span className="text-slate-600">Subscription</span>
        <span className="font-medium text-slate-900">Doctor's Journal Plus</span>
      </div>
      <div className="flex justify-between mb-1">
        <span className="text-slate-600">Sponsored by</span>
        <span className="font-medium text-slate-900">{provider.providerName}</span>
      </div>
      {subsidized > 0 && (
        <div className="flex justify-between mb-1">
          <span className="text-slate-600">Subsidy covers</span>
          <span className="font-medium text-green-700">−NZ$ {subsidized.toFixed(2)}/mo</span>
        </div>
      )}
      <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
        <span className="text-slate-700 font-semibold">Your portion (monthly)</span>
        <span className="font-bold text-slate-900">
          NZ$ {youPay.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function PaymentForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { provider, identity, claimToken, setResult } = useClaim();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !provider || !identity || !claimToken) return;
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }
    setSubmitting(true);
    setError(null);

    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Card details are required.');
      setSubmitting(false);
      return;
    }

    // Create PaymentMethod client-side. The CF attaches it to the customer
    // it creates, sets it as default, then opens the subscription.
    const pmResult = await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name: `${identity.firstName} ${identity.lastName ?? ''}`.trim(),
        email: identity.email,
      },
    });

    if (pmResult.error) {
      setError(pmResult.error.message ?? 'Card was declined.');
      setSubmitting(false);
      return;
    }

    try {
      const claim = callable<
        {
          token: string;
          paymentMethodId?: string;
          cardholderName?: string;
          turnstileToken?: string;
        },
        {
          success: boolean;
          reason?: 'invalid_code' | 'no_match' | 'already_claimed' | 'payment_failed' | 'rate_limited' | 'server_error' | 'captcha_failed' | 'requires_action' | 'invalid_link' | 'link_sent';
          entitlementId?: string;
          requiresAction?: boolean;
          clientSecret?: string;
          providerName?: string;
          monthlyPriceCents?: number;
          patientPortionCents?: number;
          noPaymentRequired?: boolean;
        }
      >('claimEntitlement');

      const r = await claim({
        token: claimToken,
        paymentMethodId: pmResult.paymentMethod.id,
        turnstileToken: turnstileToken ?? undefined,
      });

      // The member's bank wants them to approve the payment (3-D Secure).
      // Stripe shows the bank's screen; then the server confirms the claim.
      // 13 Oct 2026 — before, these cards were simply declined.
      if (!r.success && r.requiresAction && r.clientSecret && r.entitlementId) {
        const action = await stripe.handleNextAction({ clientSecret: r.clientSecret });
        if (action.error) {
          turnstileRef.current?.reset();
          setTurnstileToken(null);
          setError(action.error.message ?? 'Your bank did not approve the payment. No money was taken.');
          setSubmitting(false);
          return;
        }
        const confirm = callable<{ entitlementId: string }, typeof r>('confirmClaim');
        const c = await confirm({ entitlementId: r.entitlementId });
        if (!c.success) {
          turnstileRef.current?.reset();
          setTurnstileToken(null);
          setError(reasonToMessage(c.reason));
          setSubmitting(false);
          return;
        }
        Object.assign(r, c);
      }

      if (!r.success || !r.entitlementId) {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setError(reasonToMessage(r.reason));
        setSubmitting(false);
        return;
      }

      setResult({
        entitlementId: r.entitlementId,
        providerName: r.providerName ?? provider.providerName,
        patientPortionCents: r.patientPortionCents ?? provider.patientPortionCents,
        noPaymentRequired: !!r.noPaymentRequired,
      });
      navigate('/confirmation');
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const cardOptions = useMemo(
    () => ({
      // CO 2026-06-01: hide the postal-code field. Stripe defaults to
      // US 5-digit ZIP validation in test mode and rejects NZ 4-digit
      // postcodes (CO's real 3015 was bouncing). Stripe doesn't actually
      // require postcode for the subscription — billing address is
      // attached at customer-creation time CF-side.
      hidePostalCode: true,
      style: {
        base: {
          fontSize: '16px',
          color: '#0f172a',
          '::placeholder': { color: '#94a3b8' },
        },
        invalid: { color: '#b91c1c' },
      },
    }),
    [],
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="border border-slate-300 rounded-md px-3 py-3 mb-3 bg-white">
        <CardElement options={cardOptions} />
      </div>
      {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded mt-1 mb-3">{error}</div>}
      {TURNSTILE_SITE_KEY && (
        <div className="mb-3">
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
            options={{ theme: 'light', size: 'flexible' }}
          />
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-slate-300 text-white font-semibold py-3 rounded-md mt-1 transition-colors"
      >
        {submitting ? 'Processing…' : 'Pay & activate'}
      </button>
      <p className="text-xs text-slate-500 mt-3 text-center">
        Powered by Stripe. We never see or store your card details.
      </p>
    </form>
  );
}

function ZeroDollarFlow() {
  const navigate = useNavigate();
  const { provider, identity, claimToken, setResult } = useClaim();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  if (!provider || !identity || !claimToken) return null;

  const submit = async () => {
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const claim = callable<any, any>('claimEntitlement');
      const r = await claim({
        // No paymentMethodId — provider covers 100%. The link says who and which provider.
        token: claimToken,
        turnstileToken: turnstileToken ?? undefined,
      });
      if (!r.success || !r.entitlementId) {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setError(reasonToMessage(r.reason));
        setSubmitting(false);
        return;
      }
      setResult({
        entitlementId: r.entitlementId,
        providerName: r.providerName ?? provider.providerName,
        patientPortionCents: 0,
        noPaymentRequired: true,
      });
      navigate('/confirmation');
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 mb-3">All set</h1>
      <p className="text-slate-700 mb-6">
        <span className="font-semibold">{provider.providerName}</span> covers the full cost — no payment from you.
      </p>
      {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded mb-3">{error}</div>}
      {TURNSTILE_SITE_KEY && (
        <div className="mb-3">
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
            options={{ theme: 'light', size: 'flexible' }}
          />
        </div>
      )}
      <Button variant="primary" fullWidth onClick={submit} disabled={submitting}>
        {submitting ? 'Activating…' : 'Activate my subscription'}
      </Button>
      <div className="mt-3">
        <Button variant="ghost" onClick={() => navigate('/')}>Back</Button>
      </div>
    </div>
  );
}

function reasonToMessage(reason?: string): string {
  switch (reason) {
    case 'invalid_code':
      return "That code didn't work. Please check and try again.";
    case 'no_match':
      return "We couldn't match your details to the provider's roster. Please double-check your name, date of birth, and NHI.";
    case 'already_claimed':
      return "This place has already been claimed, or a claim for it is in progress. If that wasn't you, please contact your provider.";
    case 'payment_failed':
      return "Your payment couldn't be processed, and no money was taken. Please try a different card.";
    case 'invalid_link':
      return "This link can't be used any more (it may have expired or already been used). Please start again to get a new link.";
    case 'rate_limited':
      return "Too many attempts — please wait a moment and try again.";
    case 'captcha_failed':
      return "Verification failed — please try again.";
    default:
      return "Something went wrong. Please try again.";
  }
}
